import PopupState, { bindPopover, bindTrigger } from "material-ui-popup-state";
import React, { useState } from "react";
import { graphql, readInlineData } from "react-relay";
import { findFirstNonEmptyString } from "scmp-sdk";
import { useBookmarkButton } from "scmp-sdk/components/bookmark-button/hooks";
import type { ReadingHistoryWithArticle } from "scmp-sdk/components/dashboard-widget/display/reading-history-panel/hooks";
import { useReadingHistoryOperations } from "scmp-sdk/components/dashboard-widget/display/reading-history-panel/hooks";

import type { readingHistoryActionMenuBookmarkButtonArticle$key } from "scmp-app/queries/__generated__/readingHistoryActionMenuBookmarkButtonArticle.graphql";
import type { readingHistoryActionMenuSocialButtonArticle$key } from "scmp-app/queries/__generated__/readingHistoryActionMenuSocialButtonArticle.graphql";

import {
  ActionDivider,
  ActionMenu,
  MenuPopper,
  RemoveButton,
  StyledBookmarkButton,
  StyledShareButton,
} from "./styles";

type Props = {
  children: (state: {
    bindTrigger: (item: ReadingHistoryWithArticle) => ReturnType<typeof bindTrigger>;
    isCurrentItem: (item: ReadingHistoryWithArticle) => boolean;
  }) => React.ReactNode;
};

export const ReadingHistoryActionMenu: React.FunctionComponent<Props> = ({ children }) => {
  const { removeReadingHistory } = useReadingHistoryOperations();
  const [currentItem, setCurrentItem] = useState<ReadingHistoryWithArticle>();
  const { bindBookmarkButton } = useBookmarkButton();

  return (
    <PopupState popupId="reading-history-item-popover" variant="popover">
      {popupState => {
        const popupTriggerProps = bindTrigger(popupState);
        const bindPopupTriggerWithItem = (item: ReadingHistoryWithArticle) => {
          const props = { ...popupTriggerProps };
          props.onClick = event => {
            setCurrentItem(item);
            popupTriggerProps.onClick(event);
          };
          return props;
        };

        const popoverProps = bindPopover(popupState);
        const closePopup = () => {
          popoverProps.onClose();
          setTimeout(() => {
            setCurrentItem(undefined);
          }, 500);
        };
        const bindPopoverWithItem = () => {
          const props = { ...popoverProps };
          props.onClose = closePopup;
          return props;
        };

        const isCurrentItem = (item: ReadingHistoryWithArticle) =>
          popupState.isOpen &&
          !!currentItem &&
          currentItem.entityUuid === item.entityUuid &&
          currentItem.createdAt === item.createdAt;

        const renderSocialButtons = (item: ReadingHistoryWithArticle) => {
          const article = readInlineData<readingHistoryActionMenuSocialButtonArticle$key>(
            graphql`
              fragment readingHistoryActionMenuSocialButtonArticle on Article @inline {
                entityUuid
                entityId
                urlAlias
                headline
                socialHeadline
              }
            `,
            item.article,
          );

          const socialShareTypes = ["facebook", "twitter", "copy-link", "email"] as const;

          return socialShareTypes.map(type => (
            <StyledShareButton
              extraProps={{
                copyLink: {
                  disablePortal: false,
                  tooltipPlacement: "top",
                  variant: "tooltip",
                },
              }}
              key={type}
              onClick={() => {
                closePopup();
              }}
              position="history"
              relativeUrl={article.urlAlias}
              shortUrl={article.urlAlias}
              showLabel={false}
              style={{
                /* stylelint-disable csstree/validator */
                /* stylelint-disable property-no-unknown */
                backgroundColor: "transparent",
                svgTransform: "scale(0.75)",
              }}
              title={findFirstNonEmptyString(article.socialHeadline, article.headline)}
              type={type}
              utm={{
                campaign: article.entityId,
                medium: "share_widget",
              }}
            />
          ));
        };

        const renderBookmarkButton = (item: ReadingHistoryWithArticle) => {
          const article = readInlineData<readingHistoryActionMenuBookmarkButtonArticle$key>(
            graphql`
              fragment readingHistoryActionMenuBookmarkButtonArticle on Article @inline {
                urlAlias
                entityId
              }
            `,
            item.article,
          );

          return (
            <StyledBookmarkButton
              {...bindBookmarkButton(
                {
                  entityId: article.entityId,
                  trackings: {
                    ga4Events: {
                      click: {
                        action: "click",
                        category: "bookmark",
                        customized_parameters: {
                          action_type: "add",
                          article_id: article.entityId,
                          trigger_point: "history",
                        },
                        subcategory: "",
                      },
                    },
                  },
                },
                () => {
                  setTimeout(closePopup, 500);
                },
              )}
            />
          );
        };

        return (
          <>
            {children({ bindTrigger: bindPopupTriggerWithItem, isCurrentItem })}
            <MenuPopper
              anchorOrigin={{
                horizontal: "left",
                vertical: "top",
              }}
              elevation={4}
              transformOrigin={{
                horizontal: "left",
                vertical: "bottom",
              }}
              {...bindPopoverWithItem()}
            >
              {currentItem ? (
                <ActionMenu>
                  {renderBookmarkButton(currentItem)}
                  <ActionDivider />
                  {renderSocialButtons(currentItem)}
                  <ActionDivider />
                  <RemoveButton
                    onClick={() => {
                      closePopup();
                      void removeReadingHistory(currentItem.article, currentItem.createdAt);
                    }}
                  >
                    Remove
                  </RemoveButton>
                </ActionMenu>
              ) : null}
            </MenuPopper>
          </>
        );
      }}
    </PopupState>
  );
};

ReadingHistoryActionMenu.displayName = "ReadingHistoryActionMenu";
